import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { Icon, Stack, Text } from '@/design-system'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import { ALL_NAV_ITEMS } from '@/app/navigation/nav-config'
import { formatMoney } from '@/lib/money'
import { cn } from '@/lib/cn'
import styles from './SearchDialog.module.css'

interface Result {
  readonly id: string
  readonly group: string
  readonly icon: string
  readonly title: string
  readonly detail: string
  readonly to: string
}

const matches = (haystack: string, needle: string) =>
  haystack.toLowerCase().includes(needle.toLowerCase())

export function SearchDialog() {
  const navigate = useNavigate()
  const repository = useRepository()
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const [cursor, setCursor] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const accounts = useQuery(() => repository.listAccounts(), [repository])
  const transactions = useQuery(() => repository.listTransactions(), [repository])
  const recipients = useQuery(() => repository.listRecipients(), [repository])

  // ⌘K / Ctrl-K opens from anywhere.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setOpen((value) => !value)
      }
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  useEffect(() => {
    if (open) {
      setTerm('')
      setCursor(0)
      window.setTimeout(() => inputRef.current?.focus(), 20)
    }
  }, [open])

  const results = useMemo<readonly Result[]>(() => {
    const query = term.trim()
    const pages: Result[] = ALL_NAV_ITEMS.filter(
      (item) => query === '' || matches(item.label, query),
    ).map((item) => ({
      id: `page-${item.to}`,
      group: 'Pages',
      icon: item.icon,
      title: item.label,
      detail: item.to,
      to: item.to,
    }))

    if (query === '') return pages.slice(0, 7)

    const accountHits: Result[] = (accounts.data ?? [])
      .filter((account) => matches(`${account.label} ${account.currency} ${account.rail}`, query))
      .map((account) => ({
        id: `acc-${account.id}`,
        group: 'Accounts',
        icon: 'account_balance',
        title: `${account.label} · ${account.currency}`,
        detail: `${formatMoney(account.balance)} · ${account.rail}`,
        to: '/accounts',
      }))

    const peopleHits: Result[] = (recipients.data ?? [])
      .filter((person) => matches(person.name, query))
      .map((person) => ({
        id: `rcp-${person.id}`,
        group: 'People',
        icon: 'person',
        title: person.name,
        detail: person.maskedAccount,
        to: '/transfers/send',
      }))

    const txHits: Result[] = (transactions.data ?? [])
      .filter((row) => matches(`${row.counterparty} ${row.category}`, query))
      .slice(0, 6)
      .map((row) => ({
        id: `tx-${row.id}`,
        group: 'Transactions',
        icon: row.icon,
        title: row.counterparty,
        detail: `${row.category} · ${formatMoney(row.amount)}`,
        to: '/transactions',
      }))

    return [...pages, ...accountHits, ...peopleHits, ...txHits].slice(0, 12)
  }, [term, accounts.data, recipients.data, transactions.data])

  const go = (result: Result | undefined) => {
    if (!result) return
    setOpen(false)
    navigate(result.to)
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setCursor((value) => Math.min(value + 1, results.length - 1))
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setCursor((value) => Math.max(value - 1, 0))
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      go(results[cursor])
    }
  }

  let lastGroup = ''

  return (
    <>
      <button type="button" className={styles.trigger} onClick={() => setOpen(true)}>
        <Icon name="search" size={16} />
        <span className={styles.triggerLabel}>Search accounts, people, transactions</span>
        <kbd className={styles.kbd}>⌘K</kbd>
      </button>

      <button type="button" className={styles.compact} onClick={() => setOpen(true)} aria-label="Search">
        <Icon name="search" size={18} />
      </button>

      {open && (
        <div
          className={styles.overlay}
          role="presentation"
          onPointerDown={(event) => event.target === event.currentTarget && setOpen(false)}
        >
          <div className={styles.dialog} role="dialog" aria-modal="true" aria-label="Search">
            <div className={styles.field}>
              <Icon name="search" size={20} />
              <input
                ref={inputRef}
                className={styles.input}
                value={term}
                onChange={(event) => {
                  setTerm(event.target.value)
                  setCursor(0)
                }}
                onKeyDown={onKeyDown}
                placeholder="Search accounts, people, transactions"
                aria-label="Search"
              />
              {term && (
                <button type="button" onClick={() => setTerm('')} aria-label="Clear search">
                  <Icon name="close" size={18} />
                </button>
              )}
            </div>

            <div className={styles.results}>
              {results.length === 0 && (
                <Stack align="center" gap={2} style={{ padding: 'var(--pb-space-8)' }}>
                  <Icon name="search_off" size={22} />
                  <Text variant="caption" tone="secondary">
                    Nothing matches “{term}”.
                  </Text>
                </Stack>
              )}
              {results.map((result, index) => {
                const showGroup = result.group !== lastGroup
                lastGroup = result.group
                return (
                  <div key={result.id}>
                    {showGroup && (
                      <div className={styles.group}>
                        <Text variant="eyebrow" weight="semibold" tone="tertiary">
                          {result.group}
                        </Text>
                      </div>
                    )}
                    <button
                      type="button"
                      className={cn(styles.result, index === cursor && styles.active)}
                      onPointerEnter={() => setCursor(index)}
                      onClick={() => go(result)}
                    >
                      <span className={styles.glyph}>
                        <Icon name={result.icon} size={17} />
                      </span>
                      <Stack grow gap={0}>
                        <Text variant="caption" weight="semibold" truncate>
                          {result.title}
                        </Text>
                        <Text variant="eyebrow" tone="secondary" truncate>
                          {result.detail}
                        </Text>
                      </Stack>
                      <Icon name="subdirectory_arrow_left" size={15} />
                    </button>
                  </div>
                )
              })}
            </div>

            <div className={styles.foot}>
              {[
                ['↑↓', 'to move'],
                ['↵', 'to open'],
                ['esc', 'to close'],
              ].map(([key, label]) => (
                <Stack key={key} direction="row" align="center" gap={2}>
                  <kbd className={styles.kbd}>{key}</kbd>
                  <Text variant="eyebrow" tone="tertiary">
                    {label}
                  </Text>
                </Stack>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
